// Global state for "Entity Debug Mode" which surfaces raw entity IDs and
// relationship edges in the workflow UI.
//
// Can be activated via:
//   1. URL — navigate to any page with `?debug` (or `?debug=1`).
//   2. Keyboard chord — Cmd+E then Cmd+D within 1.5 s (Ctrl on non-Mac).
//   3. Control panel toggle.
//
// State is backed by the `debug` URL query param so it can be shared via
// copy-paste. The URL is updated in-place (replaceState, no reload).

type Listener = (enabled: boolean) => void

let debugEnabled: boolean = new URLSearchParams(window.location.search).has("debug")
const listeners: Set<Listener> = new Set()

function syncUrl(): void {
  const url = new URL(window.location.href)
  if (debugEnabled) {
    url.searchParams.set("debug", "1")
  } else {
    url.searchParams.delete("debug")
  }
  window.history.replaceState(window.history.state, "", url.toString())
}

function notify(): void {
  for (const fn of listeners) {
    fn(debugEnabled)
  }
}

export function isEntityDebugEnabled(): boolean {
  return debugEnabled
}

export function setEntityDebugEnabled(enabled: boolean): void {
  if (debugEnabled === enabled) {
    return
  }
  debugEnabled = enabled
  syncUrl()
  notify()
}

export function toggleEntityDebug(): void {
  setEntityDebugEnabled(!debugEnabled)
}

/** Subscribe to changes. Returns an unsubscribe function. */
export function onEntityDebugChange(fn: Listener): () => void {
  listeners.add(fn)
  return () => {
    listeners.delete(fn)
  }
}

// ---------------------------------------------------------------------------
// Keyboard chord: Cmd+E → Cmd+D  (Ctrl on non-Mac)
// ---------------------------------------------------------------------------

const CHORD_TIMEOUT_MS = 1_500
let awaitingSecond = false
let chordTimer: ReturnType<typeof setTimeout> | undefined

function resetChord(): void {
  awaitingSecond = false
  clearTimeout(chordTimer)
  chordTimer = undefined
}

function isEditableTarget(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) {
    return false
  }
  const tag = target.tagName.toLowerCase()
  return target.isContentEditable || tag === "input" || tag === "textarea"
}

function onKeyDown(e: KeyboardEvent): void {
  if (isEditableTarget(e.target)) {
    return
  }

  const mod = e.metaKey || e.ctrlKey
  if (!mod) {
    return
  }

  if (!awaitingSecond && e.key.toLowerCase() === "e") {
    e.preventDefault()
    awaitingSecond = true
    chordTimer = setTimeout(resetChord, CHORD_TIMEOUT_MS)
    return
  }

  if (awaitingSecond) {
    if (e.key.toLowerCase() === "d") {
      e.preventDefault()
      resetChord()
      toggleEntityDebug()
    } else {
      // Any other Cmd/Ctrl combo cancels the chord.
      resetChord()
    }
  }
}

/** Call once at app startup to install the keyboard chord listener (admin-only). */
export function installEntityDebugShortcut(isAdmin: boolean): void {
  if (!isAdmin) {
    return
  }
  window.addEventListener("keydown", onKeyDown)
}

// Sync when the user navigates back/forward.
window.addEventListener("popstate", () => {
  const fromUrl = new URLSearchParams(window.location.search).has("debug")
  if (fromUrl !== debugEnabled) {
    debugEnabled = fromUrl
    notify()
  }
})
